import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_NAME = "NV Real Estate";
const BASE_URL = "https://nvrealestate.ae";
const DEFAULT_IMAGE = `${BASE_URL}/images/logo/logo.webp`;

const routeSeo = {
  "/": {
    title: "NV Real Estate | Abu Dhabi Real Estate Agency",
    description:
      "Buy, sell, rent, and invest in Abu Dhabi real estate with NV Real Estate. Explore apartments, villas, off-plan projects, and expert property services.",
    keywords:
      "Abu Dhabi real estate, properties for sale Abu Dhabi, properties for rent Abu Dhabi, NV Real Estate, real estate agency Abu Dhabi",
  },
  "/properties-for-sale": {
    title: "Properties for Sale in Abu Dhabi | Apartments, Villas & Homes",
    description:
      "Browse properties for sale in Abu Dhabi including apartments, villas, townhouses, luxury homes, and investment properties with NV Real Estate.",
    keywords:
      "properties for sale Abu Dhabi, apartments for sale Abu Dhabi, villas for sale Abu Dhabi, buy property Abu Dhabi, Abu Dhabi real estate",
  },
  "/properties-for-rent": {
    title: "Properties for Rent in Abu Dhabi | Apartments & Villas",
    description:
      "Find properties for rent in Abu Dhabi including apartments, villas, studios, and family homes in prime communities.",
    keywords:
      "properties for rent Abu Dhabi, apartments for rent Abu Dhabi, villas for rent Abu Dhabi, rent property Abu Dhabi",
  },
  "/property-search-result": {
    title: "Abu Dhabi Property Search Results | NV Real Estate",
    description:
      "Search Abu Dhabi properties by location, price, type, bedrooms, and lifestyle needs with NV Real Estate.",
    keywords:
      "Abu Dhabi property search, search properties Abu Dhabi, UAE real estate listings, NV Real Estate listings",
  },
  "/new-projects": {
    title: "New Off-Plan Projects in Abu Dhabi | Developer Properties",
    description:
      "Explore new off-plan projects in Abu Dhabi from leading developers with payment plans, launch offers, and investment guidance.",
    keywords:
      "off-plan projects Abu Dhabi, new projects Abu Dhabi, developer properties Abu Dhabi, off-plan properties UAE",
  },
  "/luxury-collection": {
    title: "Luxury Properties in Abu Dhabi | Premium Homes & Villas",
    description:
      "Discover luxury properties in Abu Dhabi including waterfront apartments, premium villas, penthouses, and exclusive investment homes.",
    keywords:
      "luxury properties Abu Dhabi, luxury villas Abu Dhabi, premium apartments Abu Dhabi, high end real estate Abu Dhabi",
  },
  "/developers": {
    title: "Abu Dhabi Property Developers | New Project Developers",
    description:
      "Explore Abu Dhabi property developers and their latest residential communities, off-plan launches, and investment opportunities.",
    keywords:
      "Abu Dhabi developers, property developers Abu Dhabi, real estate developers UAE, off-plan developers Abu Dhabi",
  },
  "/about-us": {
    title: "About NV Real Estate | Abu Dhabi Property Experts",
    description:
      "Learn about NV Real Estate, a trusted Abu Dhabi real estate agency helping buyers, sellers, landlords, tenants, and investors.",
    keywords:
      "about NV Real Estate, Abu Dhabi real estate agency, property consultants Abu Dhabi, real estate experts UAE",
  },
  "/contact-us": {
    title: "Contact NV Real Estate | Abu Dhabi Property Consultants",
    description:
      "Contact NV Real Estate for buying, selling, renting, leasing, property management, mortgages, and real estate investment advice in Abu Dhabi.",
    keywords:
      "contact real estate agent Abu Dhabi, NV Real Estate contact, property consultant Abu Dhabi, real estate enquiry Abu Dhabi",
  },
  "/sell-your-property": {
    title: "Sell Your Property in Abu Dhabi | Free Property Valuation",
    description:
      "Sell your Abu Dhabi property with NV Real Estate. Get expert pricing, marketing, qualified buyers, and support through the full sales process.",
    keywords:
      "sell property Abu Dhabi, property valuation Abu Dhabi, sell apartment Abu Dhabi, sell villa Abu Dhabi",
  },
  "/property-management": {
    title: "Property Management in Abu Dhabi | Landlord Services",
    description:
      "Professional property management services in Abu Dhabi for landlords, investors, rental homes, apartments, and villas.",
    keywords:
      "property management Abu Dhabi, landlord services Abu Dhabi, rental management UAE, manage property Abu Dhabi",
  },
  "/mortgage": {
    title: "Mortgage Advisory in Abu Dhabi | Free Mortgage Help",
    description:
      "Get free mortgage advisory with your property purchase. Compare UAE banks, understand pre-approval, and align your mortgage with your Abu Dhabi property deal.",
    keywords:
      "mortgage Abu Dhabi, UAE mortgage advisory, mortgage consultant Abu Dhabi, home loan UAE, property finance Abu Dhabi",
  },
  "/leasing": {
    title: "Property Leasing Services in Abu Dhabi | Tenant & Landlord Support",
    description:
      "Leasing support for Abu Dhabi landlords and tenants, including rental marketing, viewings, tenant matching, and lease guidance.",
    keywords:
      "leasing Abu Dhabi, property leasing UAE, rent property Abu Dhabi, landlord leasing services",
  },
  "/sales": {
    title: "Real Estate Sales in Abu Dhabi | Buy & Sell Property",
    description:
      "Expert real estate sales support in Abu Dhabi for buyers, sellers, investors, apartments, villas, and off-plan properties.",
    keywords:
      "real estate sales Abu Dhabi, buy property Abu Dhabi, sell property Abu Dhabi, property sales UAE",
  },
  "/golden-visa-and-property-residency": {
    title: "UAE Golden Visa Property Investment | Abu Dhabi Real Estate",
    description:
      "Learn how Abu Dhabi property investment may support UAE Golden Visa eligibility with guidance from NV Real Estate.",
    keywords:
      "UAE Golden Visa property, Golden Visa Abu Dhabi real estate, property investment UAE visa",
  },
  "/blogs": {
    title: "Abu Dhabi Real Estate Blog | Market Insights & Property Guides",
    description:
      "Read Abu Dhabi real estate insights, property buying guides, rental advice, investment tips, and market updates from NV Real Estate.",
    keywords:
      "Abu Dhabi real estate blog, property market UAE, real estate tips Abu Dhabi, property investment blog",
  },
  "/team": {
    title: "Our Real Estate Team | Abu Dhabi Property Consultants",
    description:
      "Meet the NV Real Estate team of Abu Dhabi property consultants helping clients buy, sell, rent, lease, and invest.",
    keywords:
      "real estate agents Abu Dhabi, property consultants Abu Dhabi, NV Real Estate team",
  },
  "/careers": {
    title: "Careers at NV Real Estate | Real Estate Jobs Abu Dhabi",
    description:
      "Explore career opportunities with NV Real Estate and build your future in Abu Dhabi real estate.",
    keywords:
      "real estate careers Abu Dhabi, property jobs UAE, NV Real Estate careers",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | NV Real Estate",
    description:
      "Read the terms and conditions for using the NV Real Estate website and services.",
    keywords: "NV Real Estate terms, website terms Abu Dhabi real estate",
  },
  "/privacy-policy": {
    title: "Privacy Policy | NV Real Estate",
    description:
      "Read how NV Real Estate collects, uses, and protects personal information submitted through the website.",
    keywords: "NV Real Estate privacy policy, real estate privacy UAE",
  },
  "/disclaimer": {
    title: "Disclaimer | NV Real Estate",
    description:
      "Read the NV Real Estate website disclaimer for property information, listings, pricing, and availability.",
    keywords: "NV Real Estate disclaimer, property listing disclaimer UAE",
  },
  "/testimonials": {
    title: "Client Testimonials | NV Real Estate Abu Dhabi",
    description:
      "Read client testimonials and experiences from buyers, sellers, landlords, tenants, and investors who worked with NV Real Estate.",
    keywords:
      "NV Real Estate reviews, Abu Dhabi real estate testimonials, property agent reviews UAE",
  },
};

const seoLandingPages = {
  "/apartments-to-buy-in-abu-dhabi": {
    title: "Apartments to Buy in Abu Dhabi | Flats for Sale",
    description:
      "Find apartments to buy in Abu Dhabi, from affordable flats to luxury waterfront homes in prime communities with NV Real Estate.",
    keywords:
      "apartments to buy in Abu Dhabi, apartments for sale Abu Dhabi, flats for sale Abu Dhabi, buy apartment Abu Dhabi",
  },
  "/buy-house-abu-dhabi": {
    title: "Buy House in Abu Dhabi | Family Homes & Villas for Sale",
    description:
      "Buy a house in Abu Dhabi with expert guidance on villas, townhouses, family homes, communities, prices, and purchase steps.",
    keywords:
      "buy house Abu Dhabi, houses for sale Abu Dhabi, family homes Abu Dhabi, villas for sale Abu Dhabi",
  },
  "/townhouse-for-sale-abu-dhabi": {
    title: "Townhouses for Sale in Abu Dhabi | Buy Townhouse Homes",
    description:
      "Explore townhouses for sale in Abu Dhabi with community insights, property options, and buying support from NV Real Estate.",
    keywords:
      "townhouses for sale Abu Dhabi, buy townhouse Abu Dhabi, townhouse communities Abu Dhabi",
  },
  "/2bhk-for-rent-abu-dhabi": {
    title: "2 BHK for Rent in Abu Dhabi | Two Bedroom Apartments",
    description:
      "Find 2 BHK apartments for rent in Abu Dhabi with suitable locations, rental budgets, family-friendly communities, and expert support.",
    keywords:
      "2 BHK for rent Abu Dhabi, two bedroom apartment rent Abu Dhabi, 2 bedroom flat Abu Dhabi",
  },
  "/one-bedroom-flat-abu-dhabi": {
    title: "One Bedroom Flat in Abu Dhabi | 1 BHK Apartments",
    description:
      "Search one bedroom flats in Abu Dhabi for rent or purchase with location guidance and updated property options.",
    keywords:
      "one bedroom flat Abu Dhabi, 1 BHK Abu Dhabi, one bedroom apartment Abu Dhabi",
  },
  "/off-plan-properties-abu-dhabi": {
    title: "Off-Plan Properties in Abu Dhabi | New Launch Projects",
    description:
      "Explore off-plan properties in Abu Dhabi including new launches, payment plans, developer projects, and investment opportunities.",
    keywords:
      "off-plan properties Abu Dhabi, new launch properties Abu Dhabi, off-plan apartments Abu Dhabi, developer projects Abu Dhabi",
  },
  "/abu-dhabi-houses-for-sale": {
    title: "Houses for Sale in Abu Dhabi | Buy Homes & Villas",
    description:
      "Browse houses for sale in Abu Dhabi including villas, townhouses, and family homes in established and freehold communities.",
    keywords:
      "Abu Dhabi houses for sale, houses for sale Abu Dhabi, buy home Abu Dhabi, villas Abu Dhabi",
  },
  "/land-for-sale-abu-dhabi": {
    title: "Land for Sale in Abu Dhabi | Residential & Investment Plots",
    description:
      "Find land for sale in Abu Dhabi including residential plots, investment land, and development opportunities.",
    keywords:
      "land for sale Abu Dhabi, plots for sale Abu Dhabi, residential land Abu Dhabi, investment land UAE",
  },
  "/furnished-studio-rent-abu-dhabi": {
    title: "Furnished Studio for Rent in Abu Dhabi | Studio Apartments",
    description:
      "Find furnished studios for rent in Abu Dhabi with convenient locations, flexible options, and rental guidance.",
    keywords:
      "furnished studio rent Abu Dhabi, studio apartment rent Abu Dhabi, furnished flat Abu Dhabi",
  },
  "/2-bedroom-apartment-rent": {
    title: "2 Bedroom Apartment for Rent in Abu Dhabi | Family Rentals",
    description:
      "Search 2 bedroom apartments for rent in Abu Dhabi with community advice, rental pricing, and expert support.",
    keywords:
      "2 bedroom apartment rent Abu Dhabi, two bedroom flat for rent Abu Dhabi, family apartment Abu Dhabi",
  },
  "/1-bedroom-apartment-rent": {
    title: "1 Bedroom Apartment for Rent in Abu Dhabi | 1 BHK Rentals",
    description:
      "Find 1 bedroom apartments for rent in Abu Dhabi with updated rental options and guidance from NV Real Estate.",
    keywords:
      "1 bedroom apartment rent Abu Dhabi, 1 BHK rent Abu Dhabi, one bedroom apartment Abu Dhabi",
  },
  "/villas-for-sale-abu-dhabi": {
  title: "Freehold Villas for Sale in Abu Dhabi | NV Real Estate",
  description: "Villas for Sale in Abu Dhabi | Buy Family & Luxury Villas | NV Real Estate.",
  keywords: "...",
},
  "/sell-your-property-in-abu-dhabi": {
    title: "Sell Your Property in Abu Dhabi | Expert Selling Support",
    description:
      "Sell your property in Abu Dhabi with pricing advice, digital marketing, qualified buyers, viewings, and transaction support.",
    keywords:
      "sell your property in Abu Dhabi, sell apartment Abu Dhabi, sell villa Abu Dhabi, property valuation Abu Dhabi",
  },
  "/villas-to-buy-abu-dhabi": {
    title: "Villas to Buy in Abu Dhabi | Luxury & Family Villas",
    description:
      "Explore villas to buy in Abu Dhabi including luxury homes, family villas, and investment properties in prime communities.",
    keywords:
      "villas to buy Abu Dhabi, buy villa Abu Dhabi, villas for sale Abu Dhabi, luxury villas Abu Dhabi",
  },
  "/commercial-villa-for-rent-abu-dhabi": {
    title: "Commercial Villa for Rent in Abu Dhabi | Business Villas",
    description:
      "Find commercial villas for rent in Abu Dhabi for offices, clinics, nurseries, and business use with leasing support.",
    keywords:
      "commercial villa for rent Abu Dhabi, business villa rent Abu Dhabi, office villa Abu Dhabi",
  },
  
  "/rental-property-abu-dhabi": {
    title: "Rental Properties in Abu Dhabi | Apartments & Villas for Rent",
    description:
      "Find rental properties in Abu Dhabi including apartments, villas, studios, and family homes across popular communities.",
    keywords:
      "rental property Abu Dhabi, properties for rent Abu Dhabi, apartments rent Abu Dhabi, villas rent Abu Dhabi",
  },
};

const cleanSlug = (value = "") => {
  return decodeURIComponent(value)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const truncate = (value, maxLength) => {
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1).trim()}...`;
};

const ensureBrand = (title) => {
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
};

const getDynamicSeo = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments[0] === "property" && segments[1]) {
    const propertyTitle = cleanSlug(segments[1]);
    return {
      title: `${propertyTitle} | Property for Sale & Rent in Abu Dhabi`,
      description: `View ${propertyTitle} with NV Real Estate. Check property details, location, price guidance, photos, agent support, and Abu Dhabi real estate advice.`,
      keywords: `${propertyTitle}, property in Abu Dhabi, Abu Dhabi real estate listing, buy property Abu Dhabi, rent property Abu Dhabi, NV Real Estate`,
    };
  }

  if (segments[0] === "off-plan-property" && segments[1]) {
    const projectTitle = cleanSlug(segments[1]);
    return {
      title: `${projectTitle} | Off-Plan Property in Abu Dhabi`,
      description: `Explore ${projectTitle}, an off-plan property opportunity in Abu Dhabi. Get developer details, project information, payment plan guidance, and investment support.`,
      keywords: `${projectTitle}, off-plan property Abu Dhabi, new project Abu Dhabi, developer project UAE, NV Real Estate`,
    };
  }

  if (segments[0] === "area-guide" && segments[1]) {
    const areaTitle = cleanSlug(segments[1]);
    return {
      title: `${areaTitle} Area Guide | Abu Dhabi Properties`,
      description: `Read the ${areaTitle} area guide for Abu Dhabi property buyers, tenants, and investors. Explore lifestyle, property options, rental demand, and community insights.`,
      keywords: `${areaTitle} area guide, ${areaTitle} properties, Abu Dhabi communities, Abu Dhabi property guide`,
    };
  }

  if (segments[0] === "blog" && segments[1]) {
    const blogTitle = cleanSlug(segments[1]);
    return {
      title: `${blogTitle} | Abu Dhabi Real Estate Blog`,
      description: `Read ${blogTitle} from NV Real Estate for Abu Dhabi property market guidance, investment tips, and real estate insights.`,
      keywords: `${blogTitle}, Abu Dhabi real estate blog, property market UAE, NV Real Estate`,
    };
  }

  if (segments[0] === "developers" && segments[1]) {
    const developerTitle = cleanSlug(segments[1]);
    return {
      title: `${developerTitle} Projects in Abu Dhabi | Developer Properties`,
      description: `Explore ${developerTitle} projects and developer properties in Abu Dhabi with NV Real Estate, including off-plan launches and investment guidance.`,
      keywords: `${developerTitle} Abu Dhabi, developer projects Abu Dhabi, off-plan properties UAE, NV Real Estate`,
    };
  }

  if (segments[0] === "team" && segments[1]) {
    const agentTitle = cleanSlug(segments[1]);
    return {
      title: `${agentTitle} | Abu Dhabi Real Estate Consultant`,
      description: `Contact ${agentTitle}, part of the NV Real Estate team, for Abu Dhabi property buying, selling, renting, and investment support.`,
      keywords: `${agentTitle}, real estate agent Abu Dhabi, property consultant Abu Dhabi, NV Real Estate`,
    };
  }

  if (segments.length === 2) {
    const areaTitle = cleanSlug(segments[0]);
    return {
      title: `${areaTitle} Properties in Abu Dhabi | Buy, Rent & Invest`,
      description: `Explore properties in ${areaTitle}, Abu Dhabi with NV Real Estate. Find apartments, villas, rentals, sale listings, and local property guidance.`,
      keywords: `${areaTitle} properties, ${areaTitle} Abu Dhabi real estate, properties for sale ${areaTitle}, properties for rent ${areaTitle}`,
    };
  }

  return null;
};

const buildSeo = (pathname) => {
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const seo = routeSeo[normalizedPath] || seoLandingPages[normalizedPath] || getDynamicSeo(normalizedPath) || {
    title: "Abu Dhabi Real Estate | Buy, Sell & Rent Property",
    description:
      "Explore Abu Dhabi real estate with NV Real Estate. Find properties for sale, rent, off-plan projects, investment opportunities, and expert property services.",
    keywords:
      "Abu Dhabi real estate, property Abu Dhabi, buy property Abu Dhabi, rent property Abu Dhabi, NV Real Estate",
  };

  return {
    title: truncate(ensureBrand(seo.title), 68),
    description: truncate(seo.description, 160),
    keywords: seo.keywords,
    canonical: `${BASE_URL}${normalizedPath === "/" ? "" : normalizedPath}`,
  };
};

function SEOManager() {
  const { pathname } = useLocation();
  const seo = buildSeo(pathname);

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={seo.canonical} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
    </Helmet>
  );
}

export default SEOManager;
